import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Calendar,
  FileText,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useAsset, useDeleteAsset } from '@/hooks'
import {
  PageHeader,
  ConfirmDialog,
  NotFound,
  HistoryTimeline,
} from '@/components'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { AssetFormDialog } from './AssetFormDialog'

export function AssetDetailPage() {
  const { assetId } = useParams<{ assetId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()

  const {
    data: asset,
    isLoading,
    error,
  } = useAsset(assetId ?? '')
  const deleteMutation = useDeleteAsset(effectiveOrgId)

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin'

  if (isLoading) {
    return <div className="text-muted-foreground">Loading asset...</div>
  }

  if (error || !asset) {
    return <NotFound onBack={() => navigate('/assets')} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/assets">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <PageHeader
          title={asset.name}
          description="Asset details and history"
          actions={
            canEdit && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            )
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Asset Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {asset.description && (
            <div className="flex items-start gap-2">
              <FileText className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>{asset.description}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              Created {new Date(asset.created_at).toLocaleDateString()}
            </span>
          </div>
        </CardContent>
      </Card>

      <HistoryTimeline
        entityType="assets"
        entityId={assetId ?? ''}
        createdAt={asset.created_at}
        updatedAt={asset.updated_at}
        updatedBy={asset.updated_by}
        updatedByName={asset.updated_by_name}
      />

      <AssetFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        asset={asset}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Asset"
        description={`Are you sure you want to delete "${asset.name}"? This will remove it from all room checklists. This action cannot be undone.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          deleteMutation.mutate(asset.id, {
            onSuccess: () => navigate('/assets'),
          })
        }}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
